// SPDX-License-Identifier: Apache-2.0
import { AbstractSigner, hexlify } from "ethers";
import { fillOptions, isCalldataEnveloped, makeTaggedProxyObject, } from "@oasisprotocol/sapphire-paratime";
export { NETWORKS } from "@oasisprotocol/sapphire-paratime";
// ----------------------------------------------------------------------------
function isEthersJsonRpcApiProvider(p) {
    return "send" in p && typeof p.send === "function";
}
function hasRequestFunction(p) {
    return "request" in p && typeof p.request === "function";
}
/// Get a JsonRpcApiProvider compatible provider from a ContractRunner
function getProviderFromContractRunner(r) {
    if (isEthersJsonRpcApiProvider(r)) {
        return r;
    }
    if (r.provider && isEthersJsonRpcApiProvider(r.provider)) {
        return r.provider;
    }
    return;
}
/**
 * Get a EIP-1193 compatible request() function from a ContractRunner
 * @param r Runner or other compatible provider
 * @returns `undefined` if there is no attached provider
 */
function makeEthereumRequestFnFromProvider(r) {
    if (hasRequestFunction(r)) {
        return r.request;
    }
    const p = getProviderFromContractRunner(r);
    if (!p)
        return;
    return async (request) => {
        const { method, params } = request;
        return p.send(method, params ?? []);
    };
}
// ----------------------------------------------------------------------------
const SAPPHIRE_WRAPPED_ETHERS_SIGNER = "#SAPPHIRE_WRAPPED_ETHERS_SIGNER";
function isWrappedSigner(p) {
    return SAPPHIRE_WRAPPED_ETHERS_SIGNER in p;
}
export class SignerHasNoProviderError extends Error {
}
function hookEthersSend(send, options, request) {
    return (async (tx) => {
        if (tx.data) {
            const cipher = await options.fetcher.cipher({ request });
            tx.data = hexlify(cipher.encryptCall(tx.data));
        }
        return send(tx);
    });
}
export function wrapEthersSigner(upstream, options) {
    if (isWrappedSigner(upstream)) {
        return upstream;
    }
    const filled_options = fillOptions(options);
    let signer;
    if (upstream.provider) {
        const provider = wrapEthersProvider(upstream.provider, filled_options);
        try {
            signer = upstream.connect(provider);
        }
        catch (e) {
            if (e.code !== "UNSUPPORTED_OPERATION")
                throw e;
            signer = upstream;
        }
    }
    else {
        signer = upstream;
    }
    // The signer must have been connected to a provider
    const request = makeEthereumRequestFnFromProvider(signer);
    if (!request) {
        throw new SignerHasNoProviderError("Signer must be connected to a provider!");
    }
    return makeTaggedProxyObject(signer, SAPPHIRE_WRAPPED_ETHERS_SIGNER, filled_options, {
        sendTransaction: hookEthersSend(signer.sendTransaction.bind(signer), filled_options, request),
        signTransaction: hookEthersSend(signer.signTransaction.bind(signer), filled_options, request),
        call: hookEthersCall(signer, "call", filled_options, request),
        estimateGas: hookEthersCall(signer, "estimateGas", filled_options, request),
        connect(provider) {
            const wp = signer.connect(provider);
            return wrapEthersSigner(wp, filled_options);
        },
        request,
    });
}
// ----------------------------------------------------------------------------
const SAPPHIRE_WRAPPED_ETHERS_PROVIDER = "#SAPPHIRE_WRAPPED_ETHERS_PROVIDER";
function isWrappedProvider(p) {
    return SAPPHIRE_WRAPPED_ETHERS_PROVIDER in p;
}
export class ContractRunnerHasNoProviderError extends Error {
}
export function wrapEthersProvider(provider, options) {
    // Already wrapped, so don't wrap it again.
    if (isWrappedProvider(provider)) {
        return provider;
    }
    const request = makeEthereumRequestFnFromProvider(provider);
    if (!request) {
        throw new Error("Couldn't make request function!");
    }
    const filled_options = fillOptions(options);
    return makeTaggedProxyObject(provider, SAPPHIRE_WRAPPED_ETHERS_PROVIDER, filled_options, {
        // Calls can be unsigned, but must be enveloped.
        call: hookEthersCall(provider, "call", filled_options, request),
        estimateGas: hookEthersCall(provider, "estimateGas", filled_options, request),
        request,
        // TODO: wrap `getSigner()`
    });
}
function isEthersSigner(upstream) {
    return upstream instanceof AbstractSigner;
}
async function sendUnsignedCall(fn, call, is_already_enveloped, cipher) {
    let call_data = call.data ?? undefined;
    if (!is_already_enveloped) {
        call_data = hexlify(cipher.encryptCall(call.data));
    }
    return await fn?.({
        ...call,
        data: call_data ? hexlify(call_data) : "0x",
    });
}
function hookEthersCall(runner, method, options, request) {
    return async (call) => {
        const cipher = await options.fetcher.cipher({ request });
        // If the calldata is already enveloped, don't wrap it in another envelope
        const is_already_enveloped = isCalldataEnveloped(call.data ?? undefined);
        const f = runner[method];
        if (!f) {
            throw new Error(`${method} not found in runner!`);
        }
        const res = await sendUnsignedCall(f.bind(runner), isEthersSigner(runner) ? await runner.populateCall(call) : call, is_already_enveloped, cipher);
        // NOTE: if it's already enveloped, caller will decrypt it (not us)
        if (!is_already_enveloped && res && typeof res === "string") {
            return cipher.decryptResult(res);
        }
        return res;
    };
}
//# sourceMappingURL=index.js.map